import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Tag} from '@/components/ui/tag'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {type Column} from '@/components/data-table'
import {FilterSelect} from '@/components/cartera/filter-select'
import {PaginatedDataTable} from '@/components/cartera/paginated-data-table'
import {useSuscripcionCicloEstadoPeriodoColumns} from '@/components/cartera/suscripcion-columns'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  useCarteraSuscripciones,
  type AdminSubscription,
  type BillingSubscriptionEstado,
} from '@/hooks/inci/cartera/use-cartera'
import {SearchInput} from '@/components/ui/search-input'

const ESTADOS: BillingSubscriptionEstado[] = ['activa', 'pendiente_pago', 'en_gracia', 'degradada', 'cancelada']

/** Read-only: el switch del servicio SST vive en el panel de empresa. */
export function SuscripcionesTab() {
  const {t} = useTranslation()
  const table = useTableState('created_at')
  const [estado, setEstado] = useState<BillingSubscriptionEstado | ''>('')
  const {data, isLoading} = useCarteraSuscripciones({
    page: table.page,
    search: table.search,
    estado,
    payerKind: '',
  })

  const cicloEstadoPeriodo = useSuscripcionCicloEstadoPeriodoColumns()

  const columns: Column<AdminSubscription>[] = [
    {
      key: 'empresa',
      header: t('cartera.suscripciones.table.empresa'),
      width: 'w-48',
      cell: (row) => (
        <Link
          to={`/admin/empresas/${row.business_id}`}
          className="block truncate font-medium text-primary hover:underline"
          data-testid={`cartera-suscripcion-empresa-${row.id}`}>
          {row.business_name}
        </Link>
      ),
    },
    {
      key: 'items',
      header: t('cartera.suscripciones.table.items'),
      width: 'w-32',
      cell: (row) => (
        <CeldaEstado
          chip={
            <div className="flex flex-wrap gap-1" data-testid={`cartera-suscripcion-items-${row.id}`}>
              {row.items.map((item) => (
                <Tag key={item.id} tone="neutral">
                  {t(`cartera.itemKind.${item.item_kind}`)}
                </Tag>
              ))}
            </div>
          }
        />
      ),
    },
    ...cicloEstadoPeriodo,
    {
      key: 'gracia',
      header: t('cartera.suscripciones.table.gracia'),
      width: 'w-28',
      cell: (row) =>
        row.dias_gracia_restantes != null ? (
          <span className="text-sm text-tone-amber-fg" data-testid={`cartera-suscripcion-gracia-${row.id}`}>
            {t('cartera.suscripciones.diasRestantes', {count: row.dias_gracia_restantes})}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
  ]

  return (
    <PaginatedDataTable
      testId="cartera-suscripciones-tab"
      columns={columns}
      data={data}
      isLoading={isLoading}
      page={table.page}
      onPageChange={table.setPage}
      emptyMessage={t('cartera.suscripciones.empty')}
      toolbar={
        <>
          <SearchInput
            value={table.searchInput}
            onChange={table.setSearchInput}
            placeholder={t('cartera.suscripciones.searchPlaceholder')}
            data-testid="cartera-suscripciones-buscar"
          />
          <FilterSelect
            value={estado}
            onChange={(v) => {
              setEstado(v)
              table.setPage(1)
            }}
            options={ESTADOS}
            optionLabel={(e) => t(`cartera.estado.${e}`)}
            ariaLabel={t('cartera.suscripciones.filtroEstado')}
            testId="cartera-suscripciones-filtro-estado"
          />
        </>
      }
    />
  )
}
